import { z } from "zod";

import {
  ExtractAtomicOperationFromDefinition,
  createAtomicOperationDefinition,
} from "../atomic-operations/AtomicOperationDefinition.js";
import { zDateTimeString } from "../dateTypes.js";
import { CellExecutionState, CellRunMode } from "../enums.js";
import { AppSessionCellId, CellId } from "../idTypeBrands.js";
import { SemanticCap } from "../semanticCaps.js";

const UPDATE_CELL_STATUS_TYPE = "UPDATE_CELL_STATUS" as const;

export const UpdateCellStatusPayload = z.object({
  appSessionCellId: AppSessionCellId,
  state: z.nativeEnum(CellExecutionState),
  traceId: z.string().optional(),
  executeTrace: z.boolean().optional(),
  /**
   * Used for tracking timestamp when this is created, for the purpose of
   * usage in metrics. There are times when we need to know the timestamp
   * when this operation was created instead of when it was applied, so that
   * we can better track user-experienced wallclock execution duration.
   * In most cases the timestamp at which cell status is updated _should_ be
   * at the time of creation. The exception is when cell execution is finished
   * (whether cancelled/errored/succeeded) -- we want to log the timestamp which
   * is closer to the user-experienced change, which is when the changed is applied.
   * **When timestampForMetric is not provided, the timestamp at which the operation
   * is applied to the DB is used instead.**
   */
  timestampForMetric: z.number().optional(),
});
export type UpdateCellStatusPayload = z.infer<typeof UpdateCellStatusPayload>;

export const UPDATE_CELL_STATUS = createAtomicOperationDefinition({
  type: UPDATE_CELL_STATUS_TYPE,
  payloadSchema: UpdateCellStatusPayload,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_APP_SESSION,
  },
  writeAuth: {
    kind: "hasSemanticCapOnIdArg",
    cap: SemanticCap.EDIT_APP_SESSION,
    idArg: "appSessionCellId",
    idType: "AppSessionCell",
  },
  alwaysSkipUndoBuffer: true,
  logSafe: ["appSessionCellId", "state", "traceId", "executeTrace"],
  conflictId: (op) =>
    `${UPDATE_CELL_STATUS_TYPE}-${op.payload.appSessionCellId}`,
  creationId: (op) =>
    `${CREATE_APP_SESSION_CELL.type}-${op.payload.appSessionCellId}`,
  create: (args: UpdateCellStatusPayload) => ({
    type: UPDATE_CELL_STATUS_TYPE,
    payload: {
      ...args,
    },
  }),
});

export type UPDATE_CELL_STATUS = ExtractAtomicOperationFromDefinition<
  typeof UPDATE_CELL_STATUS
>;

const CREATE_APP_SESSION_CELL_TYPE = "CREATE_APP_SESSION_CELL" as const;

export const CreateAppSessionCellPayload = z.object({
  appSessionCellId: AppSessionCellId,
  cellId: CellId,
});
export type CreateAppSessionCellPayload = z.infer<
  typeof CreateAppSessionCellPayload
>;

export const CREATE_APP_SESSION_CELL = createAtomicOperationDefinition({
  type: CREATE_APP_SESSION_CELL_TYPE,
  payloadSchema: CreateAppSessionCellPayload,
  readAuth: {
    kind: "and",
    and: [
      {
        kind: "hasSemanticCap",
        cap: SemanticCap.VIEW_APP_SESSION,
      },
      {
        kind: "hasSomeOfSemanticCapsOnIdArg",
        someOfCaps: [
          SemanticCap.VIEW_PROJECT_CONTENTS_FOR_APP,
          SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
        ],
        idArg: "cellId",
        idType: "Cell",
      },
    ],
  },
  writeAuth: {
    kind: "and",
    and: [
      {
        kind: "hasSemanticCap",
        cap: SemanticCap.EDIT_APP_SESSION,
      },
      {
        kind: "hasSomeOfSemanticCapsOnIdArg",
        someOfCaps: [
          SemanticCap.VIEW_PROJECT_CONTENTS_FOR_APP,
          SemanticCap.VIEW_PROJECT_CONTENTS_FOR_LOGIC,
        ],
        idArg: "cellId",
        idType: "Cell",
      },
      {
        kind: "idArgDoesNotExist",
        idArg: "appSessionCellId",
        idType: "AppSessionCell",
      },
    ],
  },
  alwaysSkipUndoBuffer: true,
  logSafe: ["appSessionCellId", "cellId"],
  conflictId: (op) =>
    `${CREATE_APP_SESSION_CELL_TYPE}-${op.payload.appSessionCellId}`,
  create: (args: CreateAppSessionCellPayload) => ({
    type: CREATE_APP_SESSION_CELL_TYPE,
    payload: {
      ...args,
    },
  }),
});

export class AppSessionCellAlreadyExistsError extends Error {
  constructor(existingAppSessionCellId: AppSessionCellId) {
    super(
      `Tried to create an appSessionCell that already existed, existing id: ${existingAppSessionCellId}`,
    );
  }
}

export type CREATE_APP_SESSION_CELL = ExtractAtomicOperationFromDefinition<
  typeof CREATE_APP_SESSION_CELL
>;

const RESTORE_APP_SESSION_CELL_TYPE = "RESTORE_APP_SESSION_CELL" as const;
const DELETE_APP_SESSION_CELL_TYPE = "DELETE_APP_SESSION_CELL" as const;

export const RestoreAppSessionCellPayload = z.object({
  appSessionCellId: AppSessionCellId,
  /**
   * What the server adds when a client that joined after the deletion cannot
   * restore the cell from its `appSessionCellId` alone.
   */
  appSessionCellRestoreInfo: z
    .object({
      cellId: CellId,
      createdDate: zDateTimeString,
      updatedDate: zDateTimeString,
      revision: z.number(),
      state: z.nativeEnum(CellExecutionState),
    })
    .nullable()
    .default(null),
});
export type RestoreAppSessionCellPayload = z.infer<
  typeof RestoreAppSessionCellPayload
>;

export const RESTORE_APP_SESSION_CELL = createAtomicOperationDefinition({
  type: RESTORE_APP_SESSION_CELL_TYPE,
  payloadSchema: RestoreAppSessionCellPayload,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_APP_SESSION,
  },
  writeAuth: {
    kind: "hasSemanticCapOnIdArg",
    cap: SemanticCap.EDIT_APP_SESSION,
    idArg: "appSessionCellId",
    idType: "AppSessionCell",
  },
  logSafe: ["appSessionCellId"],
  alwaysSkipUndoBuffer: true,
  conflictId: (op) =>
    `${DELETE_APP_SESSION_CELL_TYPE}-${RESTORE_APP_SESSION_CELL_TYPE}-${op.payload.appSessionCellId}`,
  creationId: (op) =>
    `${CREATE_APP_SESSION_CELL.type}-${op.payload.appSessionCellId}`,
  create: ({
    appSessionCellId,
    appSessionCellRestoreInfo = null,
  }: Pick<RestoreAppSessionCellPayload, "appSessionCellId"> &
    Partial<RestoreAppSessionCellPayload>) => ({
    type: RESTORE_APP_SESSION_CELL_TYPE,
    payload: {
      appSessionCellId,
      appSessionCellRestoreInfo,
    },
  }),
});

export type RESTORE_APP_SESSION_CELL = ExtractAtomicOperationFromDefinition<
  typeof RESTORE_APP_SESSION_CELL
>;

export const DeleteAppSessionCellPayload = z.object({
  appSessionCellId: AppSessionCellId,
});
export type DeleteAppSessionCellPayload = z.infer<
  typeof DeleteAppSessionCellPayload
>;

export const DELETE_APP_SESSION_CELL = createAtomicOperationDefinition({
  type: DELETE_APP_SESSION_CELL_TYPE,
  payloadSchema: DeleteAppSessionCellPayload,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_APP_SESSION,
  },
  writeAuth: {
    kind: "hasSemanticCapOnIdArg",
    cap: SemanticCap.EDIT_APP_SESSION,
    idArg: "appSessionCellId",
    idType: "AppSessionCell",
  },
  alwaysSkipUndoBuffer: true,
  logSafe: ["appSessionCellId"],
  conflictId: (op) =>
    `${DELETE_APP_SESSION_CELL_TYPE}-${RESTORE_APP_SESSION_CELL_TYPE}-${op.payload.appSessionCellId}`,
  creationId: (op) =>
    `${CREATE_APP_SESSION_CELL.type}-${op.payload.appSessionCellId}`,
  create: (args: DeleteAppSessionCellPayload) => ({
    type: DELETE_APP_SESSION_CELL_TYPE,
    payload: {
      ...args,
    },
  }),
});

export type DELETE_APP_SESSION_CELL = ExtractAtomicOperationFromDefinition<
  typeof DELETE_APP_SESSION_CELL
>;

const RUN_CELL_TYPE = "RUN_CELL" as const;

export const RunCellPayload = z.object({
  appSessionCellId: AppSessionCellId,
  cellRunMode: z.nativeEnum(CellRunMode).optional(),
  source: z.string().optional(),
  forceOverwriteCache: z.boolean().optional(),
  isPendingMagicDiffRun: z.boolean().optional(),
  executeTrace: z.boolean().optional(),
  forLogicView: z.boolean().optional(),
  maybeSkipSql: z.boolean().optional(),
  fetchPreciseRowCount: z.boolean().optional(),
  linesRun: z.tuple([z.number(), z.number()]).optional(),
});
export type RunCellPayload = z.infer<typeof RunCellPayload>;

export const RUN_CELL = createAtomicOperationDefinition({
  type: RUN_CELL_TYPE,
  payloadSchema: RunCellPayload,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_APP_SESSION,
  },
  writeAuth: {
    kind: "hasSemanticCapOnIdArg",
    cap: SemanticCap.EDIT_APP_SESSION,
    idArg: "appSessionCellId",
    idType: "AppSessionCell",
  },
  logSafe: [
    "appSessionCellId",
    "forceOverwriteCache",
    "isPendingMagicDiffRun",
    "executeTrace",
    "forLogicView",
    "cellRunMode",
    "linesRun",
  ],
  alwaysSkipUndoBuffer: true,
  conflictId: (op) =>
    `${UPDATE_CELL_STATUS.type}-${op.payload.appSessionCellId}`,
  creationId: (op) =>
    `${CREATE_APP_SESSION_CELL.type}-${op.payload.appSessionCellId}`,
  create: (args: RunCellPayload) => ({
    type: RUN_CELL_TYPE,
    payload: {
      ...args,
    },
  }),
});

export type RUN_CELL = ExtractAtomicOperationFromDefinition<typeof RUN_CELL>;

const INTERRUPT_CELL_TYPE = "INTERRUPT_CELL" as const;

export const InterruptCellPayload = z.object({
  appSessionCellId: AppSessionCellId,
});
export type InterruptCellPayload = z.infer<typeof InterruptCellPayload>;

export const INTERRUPT_CELL = createAtomicOperationDefinition({
  type: INTERRUPT_CELL_TYPE,
  payloadSchema: InterruptCellPayload,
  readAuth: {
    kind: "hasSemanticCap",
    cap: SemanticCap.VIEW_APP_SESSION,
  },
  writeAuth: {
    kind: "hasSemanticCapOnIdArg",
    cap: SemanticCap.EDIT_APP_SESSION,
    idArg: "appSessionCellId",
    idType: "AppSessionCell",
  },
  logSafe: ["appSessionCellId"],
  alwaysSkipUndoBuffer: true,
  conflictId: (op) =>
    `${UPDATE_CELL_STATUS.type}-${op.payload.appSessionCellId}`,
  creationId: (op) =>
    `${CREATE_APP_SESSION_CELL.type}-${op.payload.appSessionCellId}`,
  create: (args: InterruptCellPayload) => ({
    type: INTERRUPT_CELL_TYPE,
    payload: {
      ...args,
    },
  }),
});
export type INTERRUPT_CELL = ExtractAtomicOperationFromDefinition<
  typeof INTERRUPT_CELL
>;
